import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";
import { installOfflineAxiosInterceptor } from "@/lib/offlineAxios";
import { installAuthInterceptor } from "@/lib/authAxios";
import {
  installDiagnosticAxiosInterceptor,
  installWindowDiagnosticHandlers,
} from "@/lib/diagnosticInterceptors";
import AppErrorBoundary from "@/components/AppErrorBoundary";
import { configureFunnelMirror } from "@/utils/analytics";

// Wire the offline axios interceptor before any component fetches fire.
installOfflineAxiosInterceptor();
// Wire the global 401 → /login?session_expired=1 handler so an expired
// session never leaves the user staring at a dead-end "Not authenticated"
// toast (May 5 2026 launch-readiness fix).
installAuthInterceptor();
// Diagnostic interceptors — Feb 19, 2026. Tag every axios rejection
// with a ref ID + category, fire-and-forget a report. Wire window-
// level handlers for unhandled errors / promise rejections so a JS
// crash is never silent.
installDiagnosticAxiosInterceptor();
installWindowDiagnosticHandlers();

// Mirror funnel events to the backend so internal counts are visible
// without depending on PostHog being fully configured.
configureFunnelMirror(process.env.REACT_APP_BACKEND_URL);

// Register the app-shell service worker so the app loads even with no network.
//
// One-shot upgrade hook (Feb 9, 2026) — when a NEW service worker is
// installed, force every open tab to reload so users running an
// in-flight session never get stuck on the old bundle. Stale SWs were
// hijacking the iteration_152 → iteration_153 redeploy and serving
// users the OLD `LessonReview` bundle even after they cleared cache.
if ("serviceWorker" in navigator && process.env.NODE_ENV === "production") {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/offline-sw.js", { updateViaCache: "none" }).then((reg) => {
      // iPad/Safari can keep a tab alive for hours without performing
      // another service-worker update check. Recheck whenever the app
      // becomes usable again so a production release arrives without
      // the instructor having to flick or manually refresh the page.
      let lastUpdateCheck = 0;
      const checkForRelease = () => {
        if (document.hidden || !navigator.onLine) return;
        const now = Date.now();
        if (now - lastUpdateCheck < 15000) return;
        lastUpdateCheck = now;
        reg.update().catch(() => { /* silent */ });
      };
      checkForRelease();
      document.addEventListener("visibilitychange", checkForRelease);
      window.addEventListener("focus", checkForRelease);
      window.addEventListener("pageshow", checkForRelease);
      window.addEventListener("online", checkForRelease);
      // When the SW finds a newer version, ask it to take over
      // immediately and reload all open tabs ONCE so the user picks
      // up the new app shell on the spot.
      reg.addEventListener("updatefound", () => {
        const installing = reg.installing;
        if (!installing) return;
        installing.addEventListener("statechange", () => {
          if (
            installing.state === "installed"
            && navigator.serviceWorker.controller
          ) {
            // Existing SW had control — a new one is taking over.
            // Trigger a single reload so the page picks up the new
            // bundle and HTML.
            try {
              if (!window.__ndg_sw_reloaded__) {
                window.__ndg_sw_reloaded__ = true;
                window.location.reload();
              }
            } catch { /* silent */ }
          }
        });
      });
    }).catch(() => { /* silent */ });

    // Also listen for the "controller change" event — fires when
    // skipWaiting + clients.claim let the new SW take over an
    // already-open page. Reload to align the bundle with the SW.
    let _swReloaded = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (_swReloaded) return;
      _swReloaded = true;
      try { window.location.reload(); } catch { /* silent */ }
    });
  });
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <AppErrorBoundary>
      <App />
    </AppErrorBoundary>
  </React.StrictMode>,
);
